/**
 * individual-config.ts — constants for the Individual person dialog.
 *
 * Port from SPFx `PersonCitizenshipSection.tsx` (citizenship, visa, country,
 * VWP lists) and `EmploymentSection.tsx` (SOC-to-NAICS map, MO school list).
 *
 * Per Bill's 2026-04-23 admin-configurable architecture mandate, these MUST
 * move to SQL catalog tables:
 *   - CitizenshipStatus, CitizenshipStayPurpose, CitizenshipVisaType,
 *     CountriesOfCitizenship (per design doc §5)
 *   - SocOccupationMap (per §6)
 *   - MO schools list (catalog TBD — probably part of LookupValues or a
 *     dedicated `StudentSchoolDirectory`)
 *
 * For v0.1.36 these are hardcoded here. Port phase 2 moves them behind the
 * `useConfig()` bundle in `/v3/config/*` responses.
 */

// -----------------------------------------------------------------------------
// Citizenship status (3 options per §5)
// -----------------------------------------------------------------------------

export interface CitizenshipStatusOption {
  readonly value: string;
  readonly label: string;
}

/**
 * NOTE: these values match the shape expected by RaoPerson.citizenshipStatus —
 * `'US Citizen' | 'Permanent Resident' | 'Non-Resident Alien' | 'Other'` — NOT
 * the SPFx internal enum (`USCitizen` / `PermanentResident` / `NonResidentAlien`).
 * The v3 type string is what gets persisted in cart peopleJson + sent to Core
 * Director, so we keep the display-string shape consistent here.
 */
export const CITIZENSHIP_STATUSES: ReadonlyArray<CitizenshipStatusOption> = [
  { value: 'US Citizen', label: 'US Citizen' },
  { value: 'Permanent Resident', label: 'Permanent Resident (Green Card)' },
  { value: 'Non-Resident Alien', label: 'Non-Resident Alien' },
];

// -----------------------------------------------------------------------------
// Stay purpose (NRA-only, purpose-of-stay cards)
//
// Person-Modal consolidation (Bill 2026-06-11, LOCKED mockup v6): the
// purpose-of-stay CARDS moved here from CitizenshipCohortGate.tsx (PURPOSE_CARDS)
// when the Stage-2 cohort screen collapsed into the consolidated Individual
// body's Citizenship block. The card KEYS are load-bearing: `deriveCohortKey`
// (cohortDocTiles.ts) switches on these exact strings to derive the
// nra-work / nra-student / nra-exchange / nra-visitor tile sets.
//
// (The prior STAY_PURPOSES dropdown list — Student / Work / Spouse of US
// Citizen / Other — is retired with the Origin-tab dropdown it fed; its
// 'Spouse of US Citizen' / 'Other' values never mapped to a cohort tile set.)
// -----------------------------------------------------------------------------

export type StayPurpose = 'Work' | 'Student' | 'Exchange Visitor' | 'Visitor / Tourist';

export interface StayPurposeCard {
  readonly key: StayPurpose;
  readonly icon: string;
  readonly label: string;
  readonly description: string;
  /**
   * Bill ruling 2026-06-12 (procedure-cited) — purpose is NOT eligible to
   * open accounts. Source: "Legacy Procedure - Non-Citizen ID Requirements.txt"
   * — "Non-Acceptable Visas: B (Visitor visa / tourism), VWP". The card stays
   * VISIBLE but renders ineligible: selecting it surfaces
   * `ineligibleMessage`, derives NO document tiles, and does NOT satisfy the
   * citizenship gate.
   */
  readonly ineligible?: boolean;
  /** Plain-language FSR-facing explanation shown when an ineligible card is selected. */
  readonly ineligibleMessage?: string;
}

// Visa-class lists are EXAMPLES ("e.g."), not exhaustive catalogs — the exact
// class is read off the captured visa stamp, never typed (Bill 2026-06-12:
// an H-2A/H-2B agricultural/seasonal worker is still a Work purpose).
export const NRA_STAY_PURPOSES: ReadonlyArray<StayPurposeCard> = [
  { key: 'Work',              icon: '💼', label: 'Work',              description: 'e.g. H-1B, H-2A/H-2B, L-1, O-1, TN, E-1/2/3 — temporary work authorization' },
  { key: 'Student',           icon: '🎓', label: 'Student',           description: 'e.g. F-1, F-2, M-1 — academic / language / vocational study' },
  { key: 'Exchange Visitor',  icon: '🧳', label: 'Exchange Visitor',  description: 'e.g. J-1, J-2 — DOS-sponsored exchange / research / au pair' },
  {
    key: 'Visitor / Tourist',
    icon: '📷',
    label: 'Visitor / Tourist',
    description: 'e.g. B-1, B-2, VWP/ESTA — short-term business or pleasure',
    // Source: "Legacy Procedure - Non-Citizen ID Requirements.txt" —
    // "Non-Acceptable Visas: B (Visitor visa / tourism), VWP".
    ineligible: true,
    // Exact wording per Bill 2026-06-12.
    ineligibleMessage:
      'B-visa and Visa-Waiver visitors are not eligible for account ownership — '
      + 'Contact your supervisor for more information.',
  },
];

// -----------------------------------------------------------------------------
// Visa type (NRA-only, with eligibility flag)
//
// PROCEDURE CITATION (replaces the prior "§5" sourcing — Bill 2026-06-12):
// "Legacy Procedure - Non-Citizen ID Requirements.txt" — acceptable visas are
// the Work (H / L / O / P / Q / J / I / E / TN-TD) and Student (F / M / J)
// classes; "Non-Acceptable Visas: B (Visitor visa / tourism), VWP (Visa
// Waiver Program)". The two `eligible:false` rows below carry that ruling.
// -----------------------------------------------------------------------------

export interface VisaTypeOption {
  readonly value: string;
  readonly label: string;
  /** True if this visa is eligible for PCB account opening. */
  readonly eligible: boolean;
}

export const VISA_TYPES: ReadonlyArray<VisaTypeOption> = [
  { value: 'F-1 Student', label: 'F-1 Student', eligible: true },
  { value: 'H-1B Worker', label: 'H-1B Worker', eligible: true },
  { value: 'L-1 Transfer', label: 'L-1 Transfer', eligible: true },
  { value: 'J-1 Exchange', label: 'J-1 Exchange', eligible: true },
  { value: 'EB Immigrant', label: 'EB Immigrant', eligible: true },
  { value: 'IR Immediate Relative', label: 'IR Immediate Relative', eligible: true },
  { value: 'DV Diversity', label: 'DV Diversity', eligible: true },
  { value: 'B-1/B-2 Tourist', label: 'B-1/B-2 Tourist', eligible: false },
  { value: 'VWP/ESTA', label: 'VWP/ESTA', eligible: false },
  { value: 'Other', label: 'Other', eligible: true },
];

/**
 * Set of ineligible visa values for fast lookup.
 * Source: "Legacy Procedure - Non-Citizen ID Requirements.txt" —
 * "Non-Acceptable Visas: B (Visitor visa / tourism), VWP".
 */
export const INELIGIBLE_VISA_TYPES: ReadonlySet<string> = new Set(
  VISA_TYPES.filter((v) => !v.eligible).map((v) => v.value),
);

/**
 * Eligibility moved to SCAN TIME (Bill 2026-06-11, LOCKED mockup v6 — the
 * typed visa-class input is killed because the exact class has no Core
 * Director destination). The visa class is read off the captured US-Visa
 * stamp (Azure DI → `normalizeVisaType` → person.visaType), so the
 * ineligibility rule must evaluate EXTRACTED classes ("B-1", "B-2", "VWP",
 * "ESTA") against the same 2-entry procedure catalog above (B-1/B-2 Tourist +
 * VWP/ESTA — INELIGIBLE_VISA_TYPES, per "Legacy Procedure - Non-Citizen ID
 * Requirements.txt": "Non-Acceptable Visas: B (Visitor visa / tourism), VWP").
 *
 * The token expansion below derives the per-class match set from the catalog
 * entries themselves ('B-1/B-2 Tourist' → B-1, B-2 · 'VWP/ESTA' → VWP, ESTA)
 * so the catalog stays the single source of truth. NOTE: this catalog is an
 * admin-config candidate (CitizenshipVisaType SQL table) — deliberately NOT
 * built in this pass.
 */
const INELIGIBLE_VISA_CLASS_TOKENS: ReadonlySet<string> = new Set(
  Array.from(INELIGIBLE_VISA_TYPES).flatMap((entry) =>
    entry
      .split('/')
      .map((part) => part.trim().split(/\s+/)[0] ?? '')
      .filter((t) => t.length > 0)
      .map((t) => t.toUpperCase()),
  ),
);

/**
 * True when a visa class — extracted from a captured visa stamp OR a legacy
 * stored dropdown value — falls in the ineligible procedure catalog ("Legacy
 * Procedure - Non-Citizen ID Requirements.txt": B / VWP non-acceptable).
 * Tolerates the compact forms Azure DI returns ("B1", "B 2") by stripping
 * spaces/hyphens before comparing class tokens.
 */
export function isIneligibleVisaClass(visaClass: string | null | undefined): boolean {
  if (!visaClass) return false;
  const raw = visaClass.trim();
  if (raw.length === 0) return false;
  // Legacy stored dropdown value ('B-1/B-2 Tourist' / 'VWP/ESTA') — direct hit.
  if (INELIGIBLE_VISA_TYPES.has(raw)) return true;
  const compact = raw.toUpperCase().replace(/[\s-]+/g, '');
  for (const token of INELIGIBLE_VISA_CLASS_TOKENS) {
    if (token.replace(/[\s-]+/g, '') === compact) return true;
  }
  return false;
}

// -----------------------------------------------------------------------------
// Country of citizenship (port of SPFx COUNTRIES, 80 entries)
// -----------------------------------------------------------------------------

export interface CountryOption {
  readonly code: string;
  readonly label: string;
}

export const COUNTRIES_OF_CITIZENSHIP: ReadonlyArray<CountryOption> = [
  { code: 'AF', label: 'Afghanistan' }, { code: 'AL', label: 'Albania' },
  { code: 'DZ', label: 'Algeria' }, { code: 'AR', label: 'Argentina' },
  { code: 'AU', label: 'Australia' }, { code: 'AT', label: 'Austria' },
  { code: 'BD', label: 'Bangladesh' }, { code: 'BE', label: 'Belgium' },
  { code: 'BR', label: 'Brazil' }, { code: 'BG', label: 'Bulgaria' },
  { code: 'CA', label: 'Canada' }, { code: 'CL', label: 'Chile' },
  { code: 'CN', label: 'China' }, { code: 'CO', label: 'Colombia' },
  { code: 'HR', label: 'Croatia' }, { code: 'CU', label: 'Cuba' },
  { code: 'CZ', label: 'Czechia' }, { code: 'DK', label: 'Denmark' },
  { code: 'DO', label: 'Dominican Republic' }, { code: 'EC', label: 'Ecuador' },
  { code: 'EG', label: 'Egypt' }, { code: 'SV', label: 'El Salvador' },
  { code: 'EE', label: 'Estonia' }, { code: 'ET', label: 'Ethiopia' },
  { code: 'FI', label: 'Finland' }, { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' }, { code: 'GH', label: 'Ghana' },
  { code: 'GR', label: 'Greece' }, { code: 'GT', label: 'Guatemala' },
  { code: 'HN', label: 'Honduras' }, { code: 'HU', label: 'Hungary' },
  { code: 'IN', label: 'India' }, { code: 'ID', label: 'Indonesia' },
  { code: 'IR', label: 'Iran' }, { code: 'IQ', label: 'Iraq' },
  { code: 'IE', label: 'Ireland' }, { code: 'IL', label: 'Israel' },
  { code: 'IT', label: 'Italy' }, { code: 'JM', label: 'Jamaica' },
  { code: 'JP', label: 'Japan' }, { code: 'JO', label: 'Jordan' },
  { code: 'KE', label: 'Kenya' }, { code: 'KR', label: 'South Korea' },
  { code: 'KW', label: 'Kuwait' }, { code: 'LV', label: 'Latvia' },
  { code: 'LB', label: 'Lebanon' }, { code: 'LT', label: 'Lithuania' },
  { code: 'MY', label: 'Malaysia' }, { code: 'MX', label: 'Mexico' },
  { code: 'MA', label: 'Morocco' }, { code: 'NL', label: 'Netherlands' },
  { code: 'NZ', label: 'New Zealand' }, { code: 'NG', label: 'Nigeria' },
  { code: 'NO', label: 'Norway' }, { code: 'PK', label: 'Pakistan' },
  { code: 'PA', label: 'Panama' }, { code: 'PE', label: 'Peru' },
  { code: 'PH', label: 'Philippines' }, { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' }, { code: 'RO', label: 'Romania' },
  { code: 'RU', label: 'Russia' }, { code: 'SA', label: 'Saudi Arabia' },
  { code: 'SG', label: 'Singapore' }, { code: 'SK', label: 'Slovakia' },
  { code: 'SI', label: 'Slovenia' }, { code: 'ZA', label: 'South Africa' },
  { code: 'ES', label: 'Spain' }, { code: 'SE', label: 'Sweden' },
  { code: 'CH', label: 'Switzerland' }, { code: 'TW', label: 'Taiwan' },
  { code: 'TH', label: 'Thailand' }, { code: 'TR', label: 'Turkey' },
  { code: 'UA', label: 'Ukraine' }, { code: 'AE', label: 'UAE' },
  { code: 'GB', label: 'United Kingdom' }, { code: 'VE', label: 'Venezuela' },
  { code: 'VN', label: 'Vietnam' },
];

// -----------------------------------------------------------------------------
// VWP countries (port of SPFx VWP_CODES — 40 ISO-2 codes)
// -----------------------------------------------------------------------------

export const VWP_COUNTRIES: ReadonlyArray<string> = [
  'AD', 'AU', 'AT', 'BE', 'BN', 'CL', 'HR', 'CZ', 'DK', 'EE',
  'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IL', 'IT', 'JP',
  'KR', 'LV', 'LI', 'LT', 'LU', 'MT', 'MC', 'NL', 'NZ', 'NO',
  'PL', 'PT', 'SM', 'SG', 'SK', 'SI', 'ES', 'SE', 'CH', 'TW',
  'GB',
];

// -----------------------------------------------------------------------------
// Employment statuses (§6 matrix)
// -----------------------------------------------------------------------------

export const EMPLOYMENT_STATUSES = [
  'Employed',
  'Self Employed',
  'Retired',
  'Student',
  'Homemaker',
  'Minor',
  'Unemployed',
] as const;

export type EmploymentStatus = typeof EMPLOYMENT_STATUSES[number];

/** Statuses that auto-populate Employer field to a sentinel string. */
export const AUTO_EMPLOYER_STATUSES: ReadonlySet<string> = new Set([
  'Minor',
  'Homemaker',
  'Retired',
  'Unemployed',
]);

/** Statuses that auto-populate Occupation field to a sentinel string. */
export const AUTO_OCCUPATION_STATUSES: ReadonlySet<string> = new Set([
  'Minor',
  'Homemaker',
]);

// -----------------------------------------------------------------------------
// Visa-class employment constraints (BACKLOG-0762, Bill 2026-06-12)
//
// Federal work-eligibility rules per nonimmigrant class, expressed as
// plain-language FSR guidance (not legalese). Class source = person.visaType
// (EXTRACTION-fed off the captured visa stamp, normalized by
// normalizeVisaType) — never typed. Unknown/absent class → no rule → no
// constraints (do-not-guess).
//
// Class behaviors:
//   F-1 — student; employer typically the school. Off-campus work requires
//         prior authorization → calm advisory when employer ≠ school on file.
//   M-1 — vocational student; off-campus work prohibited during studies;
//         only post-completion Practical Training (≤6 months, field-related)
//         → advisory whenever Employed is selected.
//   J-1 — exchange visitor; employment strictly tied to the approved
//         exchange program with prior authorization → advisory on Employed.
//   F-2 / M-2 — dependents; work PROHIBITED (M-2 cannot even obtain a work
//         permit). Status dropdown LIMITED to Homemaker / Minor (default by
//         age: under 18 → Minor, else Homemaker); the existing status rules
//         (AUTO_EMPLOYER_STATUSES / AUTO_OCCUPATION_STATUSES sentinels)
//         lock/populate Occupation + Employer. Ownership is unaffected —
//         per "Legacy Procedure - Non-Citizen ID Requirements.txt" the
//         acceptable Work/Student visa classes "may have another number
//         with them which represents that they are a dependent": dependent
//         visa holders may be owners/joint owners (incl. minor accounts).
//   J-2 — dependent; "Employed" IS offered but gated: requires a captured
//         EAD (I-766). Until an EAD doc is captured, selecting Employed
//         surfaces the requirement and keeps the Work/School section
//         incomplete (soft gate — never blocks capture or save).
//
// This catalog is an admin-config candidate (SQL table alongside
// CitizenshipVisaType) — deliberately NOT built in this pass.
// -----------------------------------------------------------------------------

export interface VisaClassEmploymentRule {
  /**
   * When set, the employment Status dropdown offers ONLY these statuses for
   * the class (F-2 / M-2 dependents: Homemaker / Minor).
   */
  allowedStatuses?: ReadonlyArray<EmploymentStatus>;
  /**
   * Default applied when the current status is blank or not in
   * `allowedStatuses`. 'by-age' = under 18 → Minor, else (or DOB unknown)
   * → Homemaker.
   */
  defaultStatus?: EmploymentStatus | 'by-age';
  /** Calm plain-language advisory shown while Status = Employed. */
  employedAdvisory?: string;
  /**
   * F-1 only — `employedAdvisory` applies ONLY when the stated employer
   * doesn't match the school on file (extra.schoolName / extra.f141Employer).
   * No school on file → advisory shows (can't corroborate on-campus work).
   */
  advisoryOnlyWhenEmployerNotSchool?: boolean;
  /** Always-visible info line for the class (dependent work prohibition + ownership note). */
  infoLine?: string;
  /**
   * J-2 — 'Employed' allowed but requires a captured EAD (I-766) before the
   * Work/School section reads complete. Soft gate only.
   */
  employedRequiresEad?: boolean;
}

const DEPENDENT_OWNERSHIP_NOTE =
  'This does not affect the account itself — dependent visa holders '
  + '(including minors) may still own or co-own accounts per PCB procedure.';

export const VISA_CLASS_EMPLOYMENT_RULES: Readonly<Record<string, VisaClassEmploymentRule>> = {
  'F-1': {
    advisoryOnlyWhenEmployerNotSchool: true,
    employedAdvisory:
      'F-1 students normally work for their school. Work anywhere else needs '
      + 'advance work authorization (for example CPT or OPT) — ask to see that '
      + 'authorization before listing an outside employer.',
  },
  'M-1': {
    employedAdvisory:
      'M-1 vocational students cannot work off campus while in school. The only '
      + 'paid work allowed is approved practical training in their field after '
      + 'the program ends (up to 6 months) — confirm that approval before continuing.',
  },
  'J-1': {
    employedAdvisory:
      'J-1 exchange visitors may only work within their approved exchange '
      + 'program, with advance authorization from their program sponsor — '
      + 'confirm before continuing.',
  },
  'F-2': {
    allowedStatuses: ['Homemaker', 'Minor'],
    defaultStatus: 'by-age',
    infoLine:
      'F-2 dependents are not permitted to work in the United States, so '
      + 'Employed and Student are not offered here. ' + DEPENDENT_OWNERSHIP_NOTE,
  },
  'M-2': {
    allowedStatuses: ['Homemaker', 'Minor'],
    defaultStatus: 'by-age',
    infoLine:
      'M-2 dependents are not permitted to work in the United States (they '
      + 'cannot obtain a work permit), so Employed and Student are not offered '
      + 'here. ' + DEPENDENT_OWNERSHIP_NOTE,
  },
  'J-2': {
    employedRequiresEad: true,
    employedAdvisory:
      'J-2 dependents may work only after USCIS issues them a work permit '
      + '(EAD card, Form I-766). Capture the EAD document to complete this '
      + 'section.',
  },
};

/**
 * Resolve the employment rule for a person's visa class. Tolerates raw
 * extraction shapes ("F1", "f 2") by normalizing the same way
 * normalizeVisaType does (uppercase, strip spaces, hyphen before the trailing
 * digit block). Unknown/absent class → undefined → NO constraints (per
 * BACKLOG-0762: never guess a class).
 */
export function visaClassEmploymentRule(
  visaClass: string | null | undefined,
): VisaClassEmploymentRule | undefined {
  if (!visaClass) return undefined;
  const cleaned = visaClass.toUpperCase().replace(/\s+/g, '');
  if (VISA_CLASS_EMPLOYMENT_RULES[cleaned]) return VISA_CLASS_EMPLOYMENT_RULES[cleaned];
  const m = cleaned.match(/^([A-Z]+)(\d+[A-Z]?)$/);
  if (m) {
    const hyphenated = `${m[1]}-${m[2]}`;
    return VISA_CLASS_EMPLOYMENT_RULES[hyphenated];
  }
  return undefined;
}

// -----------------------------------------------------------------------------
// MO schools datalist (§6 — Student-status + student-visa override)
// -----------------------------------------------------------------------------

export const MO_SCHOOLS: ReadonlyArray<string> = [
  'Rolla Public',
  'St Patrick',
  'St James Public',
  'Newburg Public',
  'Missouri S&T',
  'Springfield Pub',
  'Willard Public',
  'Missouri State',
  'Evangel Univ',
  'Drury Univ',
  'OTC',
  'Waynesville Pub',
  'Dixon Public',
  'Salem Public',
  'Cuba Public',
  'Steelville Pub',
  'Laquey Public',
];

// -----------------------------------------------------------------------------
// SOC -> NAICS mapping (port of SPFx SOC_OCCUPATIONS, 22 entries)
// -----------------------------------------------------------------------------

export interface SocOccupation {
  readonly occupation: string;
  readonly naics: string;
  readonly sector: string;
}

export const SOC_OCCUPATIONS: ReadonlyArray<SocOccupation> = [
  { occupation: 'Farming Fishing and Forestry',      naics: '11', sector: 'Agriculture' },
  { occupation: 'Mining Quarrying Oil Gas',           naics: '21', sector: 'Mining' },
  { occupation: 'Utilities',                           naics: '22', sector: 'Utilities' },
  { occupation: 'Construction and Extraction',        naics: '23', sector: 'Construction' },
  { occupation: 'Production Manufacturing',           naics: '31', sector: 'Manufacturing' },
  { occupation: 'Wholesale Trade',                     naics: '42', sector: 'Wholesale' },
  { occupation: 'Retail Sales',                        naics: '44', sector: 'Retail' },
  { occupation: 'Transportation Material Moving',     naics: '48', sector: 'Transportation' },
  { occupation: 'Information Technology',              naics: '51', sector: 'Information' },
  { occupation: 'Financial Services',                  naics: '52', sector: 'Finance' },
  { occupation: 'Real Estate Rental Leasing',         naics: '53', sector: 'Real Estate' },
  { occupation: 'Professional Scientific Technical',  naics: '54', sector: 'Professional Services' },
  { occupation: 'Management of Companies',             naics: '55', sector: 'Management' },
  { occupation: 'Administrative Support Waste',       naics: '56', sector: 'Admin Support' },
  { occupation: 'Education Training Library',         naics: '61', sector: 'Education' },
  { occupation: 'Healthcare Support',                  naics: '62', sector: 'Healthcare' },
  { occupation: 'Arts Design Entertainment',          naics: '71', sector: 'Arts & Entertainment' },
  { occupation: 'Food Preparation Serving',           naics: '72', sector: 'Food & Hospitality' },
  { occupation: 'Personal Care and Service',          naics: '81', sector: 'Other Services' },
  { occupation: 'Office Administrative Support',      naics: '92', sector: 'Government' },
  { occupation: 'Protective Service Law Enf',         naics: '92', sector: 'Government' },
  { occupation: 'Military Specific',                   naics: '92', sector: 'Military' },
  { occupation: 'Community Social Service',           naics: '62', sector: 'Social Services' },
  { occupation: 'Legal',                               naics: '54', sector: 'Legal' },
  { occupation: 'Architecture Engineering',           naics: '54', sector: 'Engineering' },
  { occupation: 'Life Physical Social Science',       naics: '54', sector: 'Science' },
];

/** The dropdown text for an occupation: "Sector — Occupation". */
export function socOccupationLabel(o: SocOccupation): string {
  return `${o.sector} — ${o.occupation}`;
}

/**
 * SOC_OCCUPATIONS in A–Z order of the displayed label (BACKLOG-1779, Bill 2026-10-16) —
 * case-insensitive and stable. SOC_OCCUPATIONS itself stays in industry-code order and
 * the stored value (occupation) is unchanged.
 */
export const SOC_OCCUPATIONS_BY_LABEL: ReadonlyArray<SocOccupation> = SOC_OCCUPATIONS
  .map((o, i) => ({ o, i, k: socOccupationLabel(o).toLocaleLowerCase('en-US') }))
  .sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : a.i - b.i))
  .map((x) => x.o);

// -----------------------------------------------------------------------------
// Name suffixes
// -----------------------------------------------------------------------------

export const NAME_SUFFIXES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '',    label: '—' },
  { value: 'Jr',  label: 'Jr' },
  { value: 'Sr',  label: 'Sr' },
  { value: 'II',  label: 'II' },
  { value: 'III', label: 'III' },
  { value: 'IV',  label: 'IV' },
];

// -----------------------------------------------------------------------------
// Field 21 Employee Code. Core Director's own legend (Bill 2026-10-17, pasted from Core Director):
//   "0=Non-employee, 1-5=Employee Restricted, 6-9=Emp. Not Restr."
// The earlier list (Employee / Director / Officer / Principal Shareholder ...) had no Core Director source.
// -----------------------------------------------------------------------------

export const INSIDER_CODES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '0', label: '0 · Non-employee' },
  { value: '1', label: '1 · Employee Restricted' },
  { value: '2', label: '2 · Employee Restricted' },
  { value: '3', label: '3 · Employee Restricted' },
  { value: '4', label: '4 · Employee Restricted' },
  { value: '5', label: '5 · Employee Restricted' },
  { value: '6', label: '6 · Employee Not Restricted' },
  { value: '7', label: '7 · Employee Not Restricted' },
  { value: '8', label: '8 · Employee Not Restricted' },
  { value: '9', label: '9 · Employee Not Restricted' },
];

// -----------------------------------------------------------------------------
// ID type codes (§13a Individual CIP — Core Director Field 254)
// -----------------------------------------------------------------------------

export const ID_TYPE_CODES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '',  label: 'Select ID type...' },
  { value: '0', label: '0 · None' },
  // "US" (Jessica 2026-10-19, CHG-0062): staff must not take a foreign licence or ID card. Codes unchanged.
  { value: '1', label: '1 · US Driver License' },
  { value: '2', label: '2 · US State ID' },
  { value: '3', label: '3 · Passport' },
  { value: '4', label: '4 · Military ID' },
  { value: '5', label: '5 · SSN Card' },
  { value: '6', label: '6 · State Documents' },
  { value: '7', label: '7 · Birth Certificate' },
  { value: '8', label: '8 · Other' },
];
